import { useQuery } from '@tanstack/react-query';
import type { ServiceItemView } from '@wodi/shared';
import { SearchPicker, type SearchPickerStatus } from '@wodi/ui';
import { useEffect, useState } from 'react';

import { listItems } from '../api/finance.ts';
import { useOnline } from '../hooks/use-online.ts';

const MIN_CHARS = 2;

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value, ms]);
  return debounced;
}

/**
 * Type-to-search for an active service or item in the catalogue (DESIGN.md §7), for a
 * manual charge or a service point's consultation fee.
 */
export function ServicePicker({
  label,
  description,
  category,
  onSelect,
}: {
  label: string;
  description?: string;
  category?: ServiceItemView['category'];
  onSelect: (item: ServiceItemView) => void;
}) {
  const online = useOnline();
  const [text, setText] = useState('');
  const trimmed = text.trim();
  const debounced = useDebounced(trimmed, 250);
  const search = useQuery({
    queryKey: ['catalogue', 'items', { q: debounced, status: 'active', category, picker: true }],
    queryFn: () =>
      listItems({ q: debounced, status: 'active', ...(category ? { category } : {}) }, 20),
    enabled: online && debounced.length >= MIN_CHARS,
    staleTime: 30_000,
    retry: false,
  });
  let status: SearchPickerStatus;
  if (trimmed.length < MIN_CHARS) status = 'idle';
  else if (!online) status = 'offline';
  else if (debounced !== trimmed || search.isFetching || search.isPending) status = 'searching';
  else if (search.isError) status = 'error';
  else status = 'ready';

  return (
    <SearchPicker
      label={label}
      description={description ?? 'Type part of the name or the code.'}
      placeholder="Name or code"
      inputValue={text}
      onInputChange={setText}
      status={status}
      items={search.data?.items ?? []}
      itemsQuery={debounced}
      getKey={(item) => item.id}
      getTextValue={(item) => `${item.name} ${item.code}`}
      renderItem={(item) => (
        <span>
          <span className="wodi-id">{item.code}</span> {item.name}
        </span>
      )}
      isExactMatch={(item, query) => item.code.toLowerCase() === query.toLowerCase()}
      onSelect={(item) => {
        onSelect(item);
        setText('');
      }}
      emptyMessage="No active service matches. Check the spelling or the code."
      errorMessage="The catalogue couldn't be searched. Try again."
      onRetry={() => void search.refetch()}
      offlineMessage="Searching the catalogue needs a connection to Wodi."
    />
  );
}
